import {
  faBarcode,
  faBook,
  faBoxesStacked,
  faListCheck,
  faTruckRampBox,
  faWarehouse,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Page } from '@app/components';
import type { NPage } from '@app/components/Page/types/Page.main';
import { RIGHTS_PRODUCTS, RIGHTS_WAREHOUSE } from '@app/constants/accessRights';
import {
  GOODS_RECEIPT,
  GOODS_RECEIPT_KEY,
  GOODS_RECEIPT_LABEL,
  GOODS_RECEIPT_PATH,
  GOODS_UNITS_KEY,
  GOODS_UNITS_LABEL,
  GOODS_UNITS,
  GOODS_UNITS_PATH,
  NO_TOOLTIP_TITLE,
  SETTINGS,
  WAREHOUSE_OPERATIONS,
  WAREHOUSE_OPERATIONS_LABEL,
  WAREHOUSE_OPERATIONS_PATH,
  WAREHOUSE_OPERATIONS_KEY,
  WAREHOUSES_LABEL,
  WAREHOUSES_PATH,
  PRODUCT_DIRECTORY,
  PRODUCT_DIRECTORY_KEY,
  PRODUCT_DIRECTORY_LABEL,
  PRODUCT_DIRECTORY_PATH,
  WAREHOUSES,
  WAREHOUSES_KEY,
  WAREHOUSES_REGISTRY,
  WAREHOUSES_REGISTRY_KEY,
  WAREHOUSES_REGISTRY_LABEL,
  WAREHOUSES_REGISTRY_PATH,
  PRODUCTS,
  PRODUCTS_KEY,
  PRODUCTS_LABEL,
  PRODUCTS_PATH,
  ADD_PRODUCT,
  ADD_PRODUCT_KEY,
  ADD_PRODUCT_LABEL,
  ADD_PRODUCT_PATH,
  EDIT_PRODUCT,
  EDIT_PRODUCT_KEY,
  EDIT_PRODUCT_LABEL,
  IMPORT_PRODUCTS,
  IMPORT_PRODUCTS_KEY,
  IMPORT_PRODUCTS_LABEL,
  IMPORT_PRODUCTS_PATH,
  INVENTORY_KEY,
  INVENTORY_LABEL,
  INVENTORY,
  INVENTORY_PATH,
  EDIT_PRODUCT_PATH,
  CREATE_WAREHOUSE,
  CREATE_WAREHOUSE_KEY,
  CREATE_WAREHOUSE_LABEL,
  CREATE_WAREHOUSE_PATH,
  EDIT_WAREHOUSE,
  EDIT_WAREHOUSE_KEY,
  EDIT_WAREHOUSE_LABEL,
  EDIT_WAREHOUSE_PATH,
  ADD_PRODUCT_CATEGORY,
  ADD_PRODUCT_CATEGORY_KEY,
  ADD_PRODUCT_CATEGORY_LABEL,
  ADD_PRODUCT_CATEGORY_PATH,
  EDIT_PRODUCT_CATEGORY,
  EDIT_PRODUCT_CATEGORY_KEY,
  EDIT_PRODUCT_CATEGORY_LABEL,
  EDIT_PRODUCT_CATEGORY_PATH,
  PRODUCT_CATEGORIES,
  PRODUCT_CATEGORIES_KEY,
  PRODUCT_CATEGORIES_LABEL,
  PRODUCT_CATEGORIES_PATH,
  BRANDS,
  BRANDS_KEY,
  BRANDS_LABEL,
  BRANDS_PATH,
  ADD_BRAND,
  ADD_BRAND_KEY,
  ADD_BRAND_LABEL,
  ADD_BRAND_PATH,
  EDIT_BRAND,
  EDIT_BRAND_KEY,
  EDIT_BRAND_LABEL,
  EDIT_BRAND_PATH,
  SUPPLIERS,
  SUPPLIERS_KEY,
  SUPPLIERS_LABEL,
  SUPPLIERS_PATH,
  ADD_SUPPLIER,
  ADD_SUPPLIER_KEY,
  ADD_SUPPLIER_LABEL,
  ADD_SUPPLIER_PATH,
  EDIT_SUPPLIER,
  EDIT_SUPPLIER_KEY,
  EDIT_SUPPLIER_LABEL,
  EDIT_SUPPLIER_PATH,
  GOODS_WRITE_OFF_LABEL,
  GOODS_WRITE_OFF_KEY,
  GOODS_WRITE_OFF,
  GOODS_WRITE_OFF_PATH,
  GOODS_TRANSFER_KEY,
  GOODS_TRANSFER_LABEL,
  GOODS_TRANSFER,
  GOODS_TRANSFER_PATH,
  ADD_INVENTORY_LABEL,
  ADD_INVENTORY_KEY,
  ADD_INVENTORY,
  ADD_INVENTORY_PATH,
  EDIT_INVENTORY_KEY,
  EDIT_INVENTORY_LABEL,
  EDIT_INVENTORY,
  EDIT_INVENTORY_PATH,
  INVENTORY_CARD_KEY,
  INVENTORY_CARD_LABEL,
  INVENTORY_CARD,
  INVENTORY_CARD_PATH,
  DATA_TESTID_ATTRIBUTE,
  PURCHASE_PLANNING_SETTINGS_KEY,
  PURCHASE_PLANNING_SETTINGS_LABEL,
  PURCHASE_PLANNING_SETTINGS,
  PURCHASE_PLANNING_SETTINGS_PATH,
} from '@app/constants/navigation';
import {
  AddProduct,
  Brands,
  CreateWarehouse,
  GoodsUnits,
  Inventory,
  ProductCategories,
  ProductDirectory,
  Products,
  PurchasePlanningSettings,
  Suppliers,
  WarehousesRegistry,
  WarehousesRegistryHeader,
} from '@app/pages/Warehouses';
import { AddEditBrand } from '@app/pages/Warehouses/Brands/components';
import {
  AddEditInventory,
  InventoryCard,
} from '@app/pages/Warehouses/Inventory';
import { AddEditProductCategory } from '@app/pages/Warehouses/ProductCategories/components';
import { AddEditSupplier } from '@app/pages/Warehouses/Suppliers/components';
import { WarehouseOperations } from '@app/pages/Warehouses/WarehouseOperations';
import { ProductOperation } from '@app/pages/Warehouses/WarehouseOperations/components';
import { FileProvider, InventoryCardProvider } from '@app/providers';
import type { NNavigation } from '@app/types';

import { ImportFromExcel } from '@pages/Clients';

const rootLevel: NPage.LevelDataModel = {
  title: WAREHOUSES_REGISTRY_LABEL,
  path: WAREHOUSES_REGISTRY_PATH,
};

const operationsLevel: NPage.LevelDataModel = {
  title: WAREHOUSE_OPERATIONS_LABEL,
  path: WAREHOUSE_OPERATIONS_PATH,
};

const productsLevel: NPage.LevelDataModel = {
  title: PRODUCTS_LABEL,
  path: PRODUCTS_PATH,
};

const categoriesLevel: NPage.LevelDataModel = {
  title: PRODUCT_CATEGORIES_LABEL,
  path: PRODUCT_CATEGORIES_PATH,
};

const brandsLevel: NPage.LevelDataModel = {
  title: BRANDS_LABEL,
  path: BRANDS_PATH,
};

const suppliersLevel: NPage.LevelDataModel = {
  title: SUPPLIERS_LABEL,
  path: SUPPLIERS_PATH,
};

const inventoryLevel: NPage.LevelDataModel = {
  title: INVENTORY_LABEL,
  path: INVENTORY_PATH,
};

export const warehousesPages: NNavigation.Main = [
  {
    key: CREATE_WAREHOUSE_KEY,
    label: CREATE_WAREHOUSE_LABEL,
    id: CREATE_WAREHOUSE,
    path: CREATE_WAREHOUSE_PATH,
    element: (
      <Page secondLevelData={rootLevel}>
        <CreateWarehouse />
      </Page>
    ),
    issidebaritem: 'false',
    sidebarparentid: WAREHOUSES,
  },
  {
    key: EDIT_WAREHOUSE_KEY,
    label: EDIT_WAREHOUSE_LABEL,
    id: EDIT_WAREHOUSE,
    path: EDIT_WAREHOUSE_PATH,
    element: (
      <Page secondLevelData={rootLevel}>
        <CreateWarehouse />
      </Page>
    ),
    issidebaritem: 'false',
    sidebarparentid: WAREHOUSES,
  },
  {
    key: PRODUCT_CATEGORIES_KEY,
    label: PRODUCT_CATEGORIES_LABEL,
    id: PRODUCT_CATEGORIES,
    path: PRODUCT_CATEGORIES_PATH,
    title: NO_TOOLTIP_TITLE,
    element: (
      <Page
        headerContent={<ProductCategories.Header />}
        secondLevelData={rootLevel}
      >
        <ProductCategories.Page />
      </Page>
    ),
    issidebaritem: 'false',
    sidebarparentid: WAREHOUSES,
  },
  {
    key: ADD_PRODUCT_CATEGORY_KEY,
    label: ADD_PRODUCT_CATEGORY_LABEL,
    id: ADD_PRODUCT_CATEGORY,
    path: ADD_PRODUCT_CATEGORY_PATH,
    title: NO_TOOLTIP_TITLE,
    element: (
      <Page secondLevelData={rootLevel} thirdLevelData={categoriesLevel}>
        <AddEditProductCategory.Page mode="add" />
      </Page>
    ),
    issidebaritem: 'false',
    sidebarparentid: WAREHOUSES,
  },
  {
    key: EDIT_PRODUCT_CATEGORY_KEY,
    label: EDIT_PRODUCT_CATEGORY_LABEL,
    id: EDIT_PRODUCT_CATEGORY,
    path: EDIT_PRODUCT_CATEGORY_PATH,
    title: NO_TOOLTIP_TITLE,
    element: (
      <Page secondLevelData={rootLevel} thirdLevelData={categoriesLevel}>
        <AddEditProductCategory.Page mode="edit" />
      </Page>
    ),
    issidebaritem: 'false',
    sidebarparentid: WAREHOUSES,
  },
  {
    key: BRANDS_KEY,
    label: BRANDS_LABEL,
    id: BRANDS,
    path: BRANDS_PATH,
    title: NO_TOOLTIP_TITLE,
    element: (
      <Page headerContent={<Brands.Header />} secondLevelData={rootLevel}>
        <Brands.Page />
      </Page>
    ),
    issidebaritem: 'false',
    sidebarparentid: WAREHOUSES,
  },
  {
    key: ADD_BRAND_KEY,
    label: ADD_BRAND_LABEL,
    id: ADD_BRAND,
    path: ADD_BRAND_PATH,
    title: NO_TOOLTIP_TITLE,
    element: (
      <Page secondLevelData={rootLevel} thirdLevelData={brandsLevel}>
        <AddEditBrand.Page mode="add" />
      </Page>
    ),
    issidebaritem: 'false',
    sidebarparentid: WAREHOUSES,
  },
  {
    key: EDIT_BRAND_KEY,
    label: EDIT_BRAND_LABEL,
    id: EDIT_BRAND,
    path: EDIT_BRAND_PATH,
    title: NO_TOOLTIP_TITLE,
    element: (
      <Page secondLevelData={rootLevel} thirdLevelData={brandsLevel}>
        <AddEditBrand.Page mode="edit" />
      </Page>
    ),
    issidebaritem: 'false',
    sidebarparentid: WAREHOUSES,
  },
  {
    key: SUPPLIERS_KEY,
    label: SUPPLIERS_LABEL,
    id: SUPPLIERS,
    path: SUPPLIERS_PATH,
    title: NO_TOOLTIP_TITLE,
    element: (
      <Page headerContent={<Suppliers.Header />} secondLevelData={rootLevel}>
        <Suppliers.Page />
      </Page>
    ),
    issidebaritem: 'false',
    sidebarparentid: WAREHOUSES,
  },
  {
    key: ADD_SUPPLIER_KEY,
    label: ADD_SUPPLIER_LABEL,
    id: ADD_SUPPLIER,
    path: ADD_SUPPLIER_PATH,
    title: NO_TOOLTIP_TITLE,
    element: (
      <Page secondLevelData={rootLevel} thirdLevelData={suppliersLevel}>
        <AddEditSupplier.Page mode="add" />
      </Page>
    ),
    issidebaritem: 'false',
    sidebarparentid: WAREHOUSES,
  },
  {
    key: EDIT_SUPPLIER_KEY,
    label: EDIT_SUPPLIER_LABEL,
    id: EDIT_SUPPLIER,
    path: EDIT_SUPPLIER_PATH,
    title: NO_TOOLTIP_TITLE,
    element: (
      <Page secondLevelData={rootLevel} thirdLevelData={suppliersLevel}>
        <AddEditSupplier.Page mode="edit" />
      </Page>
    ),
    issidebaritem: 'false',
    sidebarparentid: WAREHOUSES,
  },
  {
    key: WAREHOUSES_KEY,
    icon: <FontAwesomeIcon icon={faWarehouse} size="sm" />,
    label: WAREHOUSES_LABEL,
    [DATA_TESTID_ATTRIBUTE]: 'warehouses',
    id: WAREHOUSES,
    path: WAREHOUSES_PATH,
    title: NO_TOOLTIP_TITLE,
    issidebaritem: 'true',
    sidebarparentid: WAREHOUSES,
    servicerightsid: RIGHTS_WAREHOUSE,
    className: 'menu-item-with-children',
    children: [
      {
        key: PRODUCT_DIRECTORY_KEY,
        label: PRODUCT_DIRECTORY_LABEL,
        [DATA_TESTID_ATTRIBUTE]: 'warehouses-product-directory',
        id: PRODUCT_DIRECTORY,
        path: PRODUCT_DIRECTORY_PATH,
        title: NO_TOOLTIP_TITLE,
        icon: <FontAwesomeIcon icon={faBook} size="sm" />,
        element: (
          <Page
            secondLevelData={rootLevel}
            headerContent={<ProductDirectory.Header />}
          >
            <ProductDirectory.Page />
          </Page>
        ),
        issidebaritem: 'true',
        sidebarparentid: PRODUCT_DIRECTORY,
        servicerightsid: RIGHTS_PRODUCTS,
      },
      {
        key: PRODUCTS_KEY,
        label: PRODUCTS_LABEL,
        [DATA_TESTID_ATTRIBUTE]: 'warehouses-products',
        id: PRODUCTS,
        path: PRODUCTS_PATH,
        title: NO_TOOLTIP_TITLE,
        icon: <FontAwesomeIcon icon={faBarcode} size="sm" />,
        element: (
          <Page secondLevelData={rootLevel} headerContent={<Products.Header />}>
            <Products.Page />
          </Page>
        ),
        issidebaritem: 'true',
        sidebarparentid: PRODUCTS,
        servicerightsid: RIGHTS_PRODUCTS,
      },
      {
        key: WAREHOUSES_REGISTRY_KEY,
        label: WAREHOUSES_REGISTRY_LABEL,
        [DATA_TESTID_ATTRIBUTE]: 'warehouses-registry',
        id: WAREHOUSES_REGISTRY,
        path: WAREHOUSES_REGISTRY_PATH,
        icon: <FontAwesomeIcon icon={faBoxesStacked} size="sm" />,
        title: NO_TOOLTIP_TITLE,
        element: (
          <Page headerContent={<WarehousesRegistryHeader />}>
            <WarehousesRegistry />
          </Page>
        ),
        issidebaritem: 'true',
        sidebarparentid: WAREHOUSES_REGISTRY,
      },
      {
        key: WAREHOUSE_OPERATIONS_KEY,
        label: WAREHOUSE_OPERATIONS_LABEL,
        [DATA_TESTID_ATTRIBUTE]: 'warehouse-operations',
        id: WAREHOUSE_OPERATIONS,
        path: WAREHOUSE_OPERATIONS_PATH,
        title: NO_TOOLTIP_TITLE,
        icon: <FontAwesomeIcon icon={faListCheck} size="sm" />,
        element: (
          <Page headerContent={<WarehouseOperations.Header />}>
            <WarehouseOperations.Page />
          </Page>
        ),
        issidebaritem: 'true',
        sidebarparentid: WAREHOUSE_OPERATIONS,
      },
      {
        key: INVENTORY_KEY,
        label: INVENTORY_LABEL,
        id: INVENTORY,
        path: INVENTORY_PATH,
        title: NO_TOOLTIP_TITLE,
        [DATA_TESTID_ATTRIBUTE]: 'warehouses-inventory',
        icon: <FontAwesomeIcon icon={faTruckRampBox} size="sm" />,
        element: (
          <Page
            secondLevelData={rootLevel}
            headerContent={<Inventory.Header />}
          >
            <Inventory.Page />
          </Page>
        ),
        issidebaritem: 'true',
        sidebarparentid: INVENTORY,
      },
    ],
  },
  {
    key: ADD_PRODUCT_KEY,
    label: ADD_PRODUCT_LABEL,
    id: ADD_PRODUCT,
    path: ADD_PRODUCT_PATH,
    title: NO_TOOLTIP_TITLE,
    element: (
      <Page secondLevelData={rootLevel} thirdLevelData={productsLevel}>
        <AddProduct />
      </Page>
    ),
    issidebaritem: 'false',
    sidebarparentid: PRODUCTS,
  },
  {
    key: EDIT_PRODUCT_KEY,
    label: EDIT_PRODUCT_LABEL,
    id: EDIT_PRODUCT,
    path: EDIT_PRODUCT_PATH,
    title: NO_TOOLTIP_TITLE,
    element: (
      <Page secondLevelData={rootLevel} thirdLevelData={productsLevel}>
        <AddProduct />
      </Page>
    ),
    issidebaritem: 'false',
    sidebarparentid: PRODUCTS,
  },
  {
    key: IMPORT_PRODUCTS_KEY,
    label: IMPORT_PRODUCTS_LABEL,
    id: IMPORT_PRODUCTS,
    path: IMPORT_PRODUCTS_PATH,
    title: NO_TOOLTIP_TITLE,
    element: (
      <FileProvider>
        <Page secondLevelData={rootLevel} thirdLevelData={productsLevel}>
          <ImportFromExcel />
        </Page>
      </FileProvider>
    ),
    issidebaritem: 'false',
    sidebarparentid: PRODUCTS,
  },
  {
    key: GOODS_UNITS_KEY,
    label: GOODS_UNITS_LABEL,
    id: GOODS_UNITS,
    path: GOODS_UNITS_PATH,
    title: NO_TOOLTIP_TITLE,
    element: (
      <Page secondLevelData={rootLevel} headerContent={<GoodsUnits.Header />}>
        <GoodsUnits.Page />
      </Page>
    ),
    issidebaritem: 'false',
    sidebarparentid: SETTINGS,
  },
  {
    key: GOODS_RECEIPT_KEY,
    label: GOODS_RECEIPT_LABEL,
    id: GOODS_RECEIPT,
    path: GOODS_RECEIPT_PATH,
    title: NO_TOOLTIP_TITLE,
    element: (
      <Page secondLevelData={rootLevel} thirdLevelData={operationsLevel}>
        <ProductOperation type="goods-receipt" />
      </Page>
    ),
    issidebaritem: 'false',
    sidebarparentid: WAREHOUSE_OPERATIONS,
  },
  {
    key: GOODS_WRITE_OFF_KEY,
    label: GOODS_WRITE_OFF_LABEL,
    id: GOODS_WRITE_OFF,
    path: GOODS_WRITE_OFF_PATH,
    title: NO_TOOLTIP_TITLE,
    element: (
      <Page secondLevelData={rootLevel} thirdLevelData={operationsLevel}>
        <ProductOperation type="goods-write-off" />
      </Page>
    ),
    issidebaritem: 'false',
    sidebarparentid: WAREHOUSE_OPERATIONS,
  },
  {
    key: GOODS_TRANSFER_KEY,
    label: GOODS_TRANSFER_LABEL,
    id: GOODS_TRANSFER,
    path: GOODS_TRANSFER_PATH,
    title: NO_TOOLTIP_TITLE,
    element: (
      <Page secondLevelData={rootLevel} thirdLevelData={operationsLevel}>
        <ProductOperation type="goods-transfer" />
      </Page>
    ),
    issidebaritem: 'false',
    sidebarparentid: WAREHOUSE_OPERATIONS,
  },
  {
    key: ADD_INVENTORY_KEY,
    label: ADD_INVENTORY_LABEL,
    id: ADD_INVENTORY,
    path: ADD_INVENTORY_PATH,
    title: NO_TOOLTIP_TITLE,
    element: (
      <Page secondLevelData={rootLevel} thirdLevelData={inventoryLevel}>
        <AddEditInventory mode="add" />
      </Page>
    ),
    issidebaritem: 'false',
    sidebarparentid: INVENTORY,
  },
  {
    key: EDIT_INVENTORY_KEY,
    label: EDIT_INVENTORY_LABEL,
    id: EDIT_INVENTORY,
    path: EDIT_INVENTORY_PATH,
    title: NO_TOOLTIP_TITLE,
    element: (
      <Page secondLevelData={rootLevel} thirdLevelData={inventoryLevel}>
        <AddEditInventory mode="edit" />
      </Page>
    ),
    issidebaritem: 'false',
    sidebarparentid: INVENTORY,
  },
  {
    key: INVENTORY_CARD_KEY,
    label: INVENTORY_CARD_LABEL,
    id: INVENTORY_CARD,
    path: INVENTORY_CARD_PATH,
    title: NO_TOOLTIP_TITLE,
    element: (
      <InventoryCardProvider>
        <Page
          headerContent={<InventoryCard.Header />}
          secondLevelData={rootLevel}
          thirdLevelData={inventoryLevel}
        >
          <InventoryCard.Page />
        </Page>
      </InventoryCardProvider>
    ),
    issidebaritem: 'false',
    sidebarparentid: INVENTORY,
  },
  {
    key: PURCHASE_PLANNING_SETTINGS_KEY,
    label: PURCHASE_PLANNING_SETTINGS_LABEL,
    id: PURCHASE_PLANNING_SETTINGS,
    path: PURCHASE_PLANNING_SETTINGS_PATH,
    title: NO_TOOLTIP_TITLE,
    element: (
      <Page secondLevelData={rootLevel}>
        <PurchasePlanningSettings />
      </Page>
    ),
    issidebaritem: 'false',
    sidebarparentid: SETTINGS,
  },
];
